/**
 * Inline Filters Component - Compact collapsible filter controls
 *
 * A compact filter bar designed to work with collapsible toolbar:
 * - Multiple filter types (select, text, date range)
 * - Minimal, horizontal layout (no header section)
 * - Individual clear buttons
 * - Active state indicators
 * - Fully accessible
 *
 * Usage:
 * ```tsx
 * <InlineFilters
 *   filters={[
 *     { id: 'status', type: 'select', label: 'Status', options: [...] },
 *     { id: 'method', type: 'select', label: 'Method', options: [...] },
 *     { id: 'date', type: 'daterange', label: 'Date Range' },
 *   ]}
 *   values={filterValues}
 *   onChange={setFilterValues}
 * />
 * ```
 */

"use client";

import { cn } from "@/lib/utils/general-utils";
import { X } from "lucide-react";

export interface FilterOption {
  label: string;
  value: string;
  /** Optional grouping key — options with the same group render under a header. */
  group?: string;
}

export interface InlineFilterConfig {
  id: string;
  type: "select" | "text" | "daterange";
  label: string;
  placeholder?: string;
  options?: FilterOption[];
  defaultValue?: string;
  /** When true, a select filter renders as multi-select (checkboxes). Defaults to single-select. */
  multiple?: boolean;
  /**
   * How the select trigger sizes its min-width.
   * `"selected"` fits the current value; `"widestOption"` expands to the
   * widest option label so chip width stays stable as the selection changes.
   */
  fitToContent?: "selected" | "widestOption";
  /** Open-menu width floor in pixels. Does not grow the closed chip. */
  minDropdownWidth?: number;
  /** Open-menu width cap in pixels. */
  maxDropdownWidth?: number;
  /** When true, the select shows an in-dropdown search field (also auto-enabled at 5+ options). */
  searchable?: boolean;
  /** When false, the chip is hidden until enabled in the filter menu. Defaults to true. */
  defaultVisible?: boolean;
  /** When true, the filter is rendered but not interactive (grayed out). */
  disabled?: boolean;
  /** Tooltip text shown when the filter is disabled. */
  disabledReason?: string;
  /**
   * Column id for toolbar sort controls when it differs from `id`.
   * Defaults to `id` (filter id must match a sortable column).
   */
  sortColumnId?: string;
  /** Combined Pass/Fail/Not assigned toolbar for the analyses Specification filter. */
  specificationOutcomeToolbar?: boolean;
  /** Combined Pass/Fail/Not assigned toolbar for the analyses Procedure filter. */
  procedureOutcomeToolbar?: boolean;
}

export interface InlineFiltersProps {
  /** Filter configurations */
  filters: InlineFilterConfig[];
  /** Current filter values */
  values: Record<string, string | string[] | { from: string; to: string }>;
  /** Change handler */
  onChange: (
    _filterValues: Record<
      string,
      string | string[] | { from: string; to: string }
    >,
  ) => void;
  /** Additional CSS classes */
  className?: string;
}

export function InlineFilters({
  filters,
  values,
  onChange,
  className = "",
}: Readonly<InlineFiltersProps>) {
  const handleFilterChange = (
    filterId: string,
    value: string | { from: string; to: string },
  ) => {
    onChange({
      ...values,
      [filterId]: value,
    });
  };

  const handleClearIndividualFilter = (filterId: string) => {
    const filter = filters.find((f) => f.id === filterId);
    if (!filter) return;

    const clearedValue =
      filter.type === "daterange"
        ? { from: "", to: "" }
        : filter.type === "select"
          ? "all"
          : "";

    onChange({
      ...values,
      [filterId]: clearedValue,
    });
  };

  const isFilterActive = (filterId: string): boolean => {
    const value = values[filterId];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "string") {
      return !!(value && value !== "all" && value !== "");
    }
    return !!(
      value &&
      typeof value === "object" &&
      (value.from !== "" || value.to !== "")
    );
  };

  return (
    <div
      className={cn("flex items-center gap-2", className)}
      role="group"
      aria-label="Filters"
    >
      {filters.map((filter) => {
        const filterIsActive = isFilterActive(filter.id);

        return (
          <div
            key={filter.id}
            title={filter.disabled ? filter.disabledReason : undefined}
          >
            {/* Filter Input */}
            {filter.type === "select" && (
              <div className="relative inline-flex items-center">
                <select
                  id={`filter-${filter.id}`}
                  value={(values[filter.id] as string) || "all"}
                  onChange={(e) =>
                    handleFilterChange(filter.id, e.target.value)
                  }
                  disabled={filter.disabled}
                  className={cn(
                    "h-9 pl-3 pr-8 text-sm font-medium border rounded-lg transition-all appearance-none",
                    "focus:outline-none",
                    "min-w-[140px]",
                    filter.disabled
                      ? "bg-gray-100 text-text-tertiary border-gray-200 cursor-not-allowed"
                      : filterIsActive
                        ? "bg-primary text-white border-primary cursor-pointer"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 cursor-pointer",
                  )}
                  aria-label={filter.label}
                >
                  <option value="all">{filter.label}: All</option>
                  {filter.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {filter.label}: {option.label}
                    </option>
                  ))}
                </select>
                {/* Custom Dropdown Arrow */}
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg
                    className={cn(
                      "h-4 w-4",
                      filter.disabled
                        ? "text-gray-300"
                        : filterIsActive
                          ? "text-white"
                          : "text-gray-400",
                    )}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            )}

            {filter.type === "text" && (
              <div className="relative inline-flex items-center">
                <input
                  id={`filter-${filter.id}`}
                  type="text"
                  value={(values[filter.id] as string) || ""}
                  onChange={(e) =>
                    handleFilterChange(filter.id, e.target.value)
                  }
                  disabled={filter.disabled}
                  placeholder={filter.placeholder || filter.label}
                  className={cn(
                    "h-9 pl-3 pr-8 text-sm font-medium border rounded-lg transition-all",
                    "focus:outline-none placeholder:text-gray-400",
                    "min-w-[140px]",
                    filter.disabled
                      ? "bg-gray-100 text-text-tertiary border-gray-200 cursor-not-allowed"
                      : filterIsActive
                        ? "bg-primary text-white border-primary placeholder:text-white/60"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400",
                  )}
                  aria-label={filter.label}
                />
                {filterIsActive && (
                  <button
                    onClick={() => handleClearIndividualFilter(filter.id)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full bg-white/20 hover:bg-white/40 transition-colors"
                    type="button"
                    aria-label={`Clear ${filter.label} filter`}
                  >
                    <X className="h-3 w-3 text-white" />
                  </button>
                )}
              </div>
            )}

            {/* daterange type is now handled by DateRangeDropdown in the toolbar */}
          </div>
        );
      })}
    </div>
  );
}
